import { FormEvent, useEffect, useState } from "react";
import { MdKey } from "react-icons/md";
import { AiOutlineCheck } from "react-icons/ai";
import { useTranslation } from "react-i18next";

import { Input } from "../../components/Input";

import * as S from "./styles";
import { InputAlign } from "../../styles";
import { Logo } from "../../components/Logo";
import { Button } from "../../../../components/Button";
import { colors } from "../../../../styles/themes/default/foundations/colors";
import { useNavigate, useSearchParams } from "react-router-dom";
import useRecoveryPassword from "../../../../hooks/useRecoveryPassword";
import { toastify } from "../../../../shared/Toastify";
import { AxiosError } from "axios";

export function NewPassword() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const email = searchParams.get("email");
  const recoveryCode = searchParams.get("recoveryCode");
  const countryCode = searchParams.get("countryCode");
  const errorMessage = searchParams.get("error");

  const navigate = useNavigate();

  const [password, setPassword] = useState("");
  const [passwordConfirmation, setPasswordConfirmation] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const { updatePassword } = useRecoveryPassword();

  const hasCapitalLetter = (password: string) => /[A-Z]/.test(password);
  const hasMinLength = (password: string) => password.length >= 6;
  const hasSpecialCharacter = (password: string) =>
    /[!@#$%^&*(),.?":{}|<>]/.test(password);
  const hasMatch = (password: string, passwordConfirmation: string) => {
    if (password === "" || passwordConfirmation === "") {
      return false;
    }
    return password === passwordConfirmation;
  };

  const passwordRequirements = {
    hasCapital: hasCapitalLetter(password),
    hasMinChar: hasMinLength(password),
    hasSpecial: hasSpecialCharacter(password),
    hasMatch: hasMatch(password, passwordConfirmation),
  };

  async function handleUpdatePassword(event: FormEvent) {
    event.preventDefault();
    if (
      !email ||
      !recoveryCode ||
      !countryCode ||
      !password ||
      !passwordConfirmation
    )
      return;

    try {
      setIsLoading(true);

      await updatePassword(
        email,
        recoveryCode,
        countryCode,
        password,
        passwordConfirmation,
      );

      navigate(`/forgot-password/new-password/success?email=${email}`);
    } catch (error) {
      let message = t("failedToUpdatePassword");

      if (error instanceof AxiosError) {
        message = error?.response?.data?.message || t("failedToUpdatePassword");
      }

      toastify("error", message, "top-right");
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    if (errorMessage) {
      navigate(
        `/forgot-password/new-password/link-expired?email=${email}&countryCode=${countryCode}&error=${errorMessage}`,
      );
    }
  }, [errorMessage]);

  const submitDisabled =
    !passwordRequirements.hasCapital ||
    !passwordRequirements.hasMinChar ||
    !passwordRequirements.hasSpecial ||
    !passwordRequirements.hasMatch;

  return (
    <S.ForgotPasswordContainer>
      <Logo />
      <S.CheckNewPasswordContainer
        onSubmit={(event) => handleUpdatePassword(event)}
      >
        <InputAlign>
          <Input
            label={t("newPassword")}
            icon={<MdKey />}
            name="password"
            value={password}
            onChange={(event) => {
              setPassword(event.target.value);
            }}
            isPassword
          />
        </InputAlign>

        <S.PassRules>{t("passwordMustHave")}</S.PassRules>

        <S.RequirementsList>
          <S.RequirementItem isValid={passwordRequirements.hasCapital}>
            <AiOutlineCheck
              size={20}
              color={passwordRequirements.hasCapital ? "#4CAF50" : "#666666"}
            />
            <span>{t("oneCapitalLetter")}</span>
          </S.RequirementItem>

          <S.RequirementItem isValid={passwordRequirements.hasMinChar}>
            <AiOutlineCheck
              size={20}
              color={passwordRequirements.hasMinChar ? "#4CAF50" : "#666666"}
            />
            <span>{t("atLeast6Characters")}</span>
          </S.RequirementItem>

          <S.RequirementItem isValid={passwordRequirements.hasSpecial}>
            <AiOutlineCheck
              size={20}
              color={passwordRequirements.hasSpecial ? "#4CAF50" : "#666666"}
            />
            <span>{t("aSpecialCharacter")}</span>
          </S.RequirementItem>

          <S.RequirementItem isValid={passwordRequirements.hasMatch}>
            <AiOutlineCheck
              size={20}
              color={passwordRequirements.hasMatch ? "#4CAF50" : "#666666"}
            />
            <span>{t("passwordsMatch")}</span>
          </S.RequirementItem>
        </S.RequirementsList>

        <Input
          label={t("confirmYourPassword")}
          icon={<MdKey />}
          name="passwordConfirmation"
          value={passwordConfirmation}
          onChange={(value) => {
            setPasswordConfirmation(value.target.value);
          }}
          isPassword
        />

        <Button
          type="submit"
          style={{
            width: "100%",
            backgroundColor: colors.white,
            color: colors["black-900"],
            marginTop: "2rem",
          }}
          loadingColor={colors["black-900"]}
          isLoading={isLoading}
          disabled={submitDisabled}
          onClick={(event) => handleUpdatePassword(event)}
        >
          <p>{t("changePassword")}</p>
        </Button>
      </S.CheckNewPasswordContainer>
    </S.ForgotPasswordContainer>
  );
}
